/**
 * Verify Invitation Page
 * Allows users to enter their OTP code to verify their invitation
 */

import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../services/supabase";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Card from "../components/ui/Card";

const VerifyInvitation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // The invitation was already used and the account is set up — offer "Log in" instead of
  // the misleading "ask your administrator to resend."
  const [alreadySetup, setAlreadySetup] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Self-service "Request a new code" inline form state
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState(
    searchParams.get("email") || "",
  );
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendNotice, setResendNotice] = useState("");
  const [resendCooldownUntil, setResendCooldownUntil] = useState(0);
  const [resendCooldownRemaining, setResendCooldownRemaining] = useState(0);

  // Tick the cooldown countdown for the disabled-button label.
  useEffect(() => {
    if (!resendCooldownUntil) {
      return;
    }
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.ceil((resendCooldownUntil - Date.now()) / 1000),
      );
      setResendCooldownRemaining(remaining);
      if (remaining === 0) {
        setResendCooldownUntil(0);
      }
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [resendCooldownUntil]);

  const handleResendSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resendSubmitting || resendCooldownRemaining > 0) {
      return;
    }
    setResendSubmitting(true);
    setResendNotice("");
    try {
      // Generic 200 response on success/failure/rate-limit branches —
      // backend enforces email-enumeration defense, frontend renders
      // a single generic message regardless of outcome.
      await supabase.functions.invoke("request-invitation-code", {
        body: { email: resendEmail.toLowerCase().trim() },
      });
    } catch (err) {
      // Network errors only — log silently; user still gets the
      // same generic message to avoid leaking failure signal.
      console.error("[VerifyInvitation] resend request error:", err);
    } finally {
      setResendNotice(
        "If a pending invitation exists for that email, a new code has been sent. Check your inbox in a minute.",
      );
      setResendCooldownUntil(Date.now() + 60_000);
      setResendSubmitting(false);
    }
  };

  // Focus first empty input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow numeric input
    const numericValue = value.replace(/[^0-9]/g, "");

    if (numericValue.length <= 1) {
      const newCode = [...code];
      newCode[index] = numericValue;
      setCode(newCode);

      // Auto-focus next input
      if (numericValue && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (numericValue.length === 6) {
      // Handle paste of full code
      const newCode = numericValue.split("");
      setCode(newCode);
      inputRefs.current[5]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      // Move to previous input on backspace when current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (pastedData.length === 6) {
      setCode(pastedData.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setAlreadySetup(false);

    const fullCode = code.join("");

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    if (fullCode.length !== 6) {
      setError("Please enter all 6 digits of your verification code");
      return;
    }

    try {
      setIsLoading(true);

      // Call the verify-invitation-code edge function
      const { data, error: fnError } = await supabase.functions.invoke(
        "verify-invitation-code",
        {
          body: { email: email.toLowerCase().trim(), code: fullCode },
        },
      );

      if (fnError) {
        console.error("[VerifyInvitation] Function error:", fnError);
        console.error("[VerifyInvitation] Response data:", data);
        // On a non-2xx status, supabase-js sets `data` to null and puts the JSON body on
        // `fnError.context` (a Response). Read it so the edge function's SPECIFIC message
        // (and the already_setup flag) actually reach the user — otherwise every failure
        // falls through to the generic "ask your administrator to resend" fallback.
        let body: { error?: string; already_setup?: boolean } | null =
          (data as { error?: string; already_setup?: boolean } | null) ?? null;
        const ctx = (fnError as { context?: unknown }).context;
        if (!body?.error && ctx instanceof Response) {
          try {
            body = await ctx.clone().json();
          } catch {
            // non-JSON body (platform error) — fall through to the generic message
          }
        }
        if (body?.already_setup) {
          // Already-set-up account (stale invitation link) → guide to login, not "resend".
          setAlreadySetup(true);
        }
        const message =
          body?.error ||
          "Verification failed. Please try again, or ask your administrator to resend the invitation.";
        throw new Error(message);
      }

      if (!data?.success) {
        throw new Error(data?.error || "Verification failed");
      }

      console.log("[VerifyInvitation] Verification successful");

      // Redirect to the magic link URL returned by the function
      // This will sign the user in and take them to setup-account
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        // Fallback - shouldn't happen
        navigate("/login?verified=success");
      }
    } catch (err: unknown) {
      console.error("[VerifyInvitation] Error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Verification failed";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="/logowhite.png"
            alt="U.S. LawShield"
            className="h-16 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Verify Your Invitation
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter the 6-digit code from your invitation email
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card padding="lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <Input
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              fullWidth
              disabled={!!searchParams.get("email")}
            />

            {/* Code Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <div className="flex justify-center gap-2" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    autoComplete="off"
                  />
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500 text-center">
                Check your email for the 6-digit code
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div
                className={`p-3 rounded-lg border ${alreadySetup ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}`}
              >
                <p
                  className={`text-sm ${alreadySetup ? "text-blue-900" : "text-red-800"}`}
                >
                  {error}
                </p>
                {alreadySetup && (
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="mt-2 w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    Go to login
                  </button>
                )}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Verify & Continue"}
            </Button>

            {/* Help Text + self-service resend */}
            <div className="text-center space-y-3">
              <p className="text-sm text-gray-500">
                Didn't receive a code or did it expire?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setResendOpen((v) => !v);
                    setResendNotice("");
                  }}
                  className="text-red-600 hover:text-red-700 font-medium"
                >
                  {resendOpen ? "Hide" : "Request a new code"}
                </button>
              </p>

              {resendOpen && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-left">
                  <p className="text-xs text-gray-600 mb-3">
                    Enter the email your invitation was sent to. We'll send a
                    new 6-digit code if there's a pending invitation.
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500"
                      autoComplete="email"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleResendSubmit}
                      isLoading={resendSubmitting}
                      disabled={
                        resendSubmitting ||
                        resendCooldownRemaining > 0 ||
                        !resendEmail.trim()
                      }
                    >
                      {resendCooldownRemaining > 0
                        ? `Wait ${resendCooldownRemaining}s`
                        : "Send new code"}
                    </Button>
                  </div>
                  {resendNotice && (
                    <p className="mt-3 text-xs text-gray-700">{resendNotice}</p>
                  )}
                  <p className="mt-3 text-xs text-gray-500">
                    Still stuck?{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="text-red-600 hover:text-red-700 font-medium"
                    >
                      Contact your administrator
                    </button>
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Back to login
              </button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default VerifyInvitation;
